/* eslint-disable react-doctor/no-initialize-state, react-doctor/no-multi-comp, react-hooks-js/refs, react-hooks-js/set-state-in-effect */

import type { DbClient } from "@tanstack/react-db";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { SpeedInsights } from "@vercel/speed-insights/react";
import {
    HeadContent,
    Outlet,
    Scripts,
    useRouterState,
} from "@tanstack/react-router";
import { EDGE_COLORS } from "@wags/ui/edge-color.ts";
import grenetteHeroFont from "@wags/ui/fonts/optimized/GrenettePro-Regular-all.woff2?url&no-inline";
import googleSansFlexLatinFont from "@wags/ui/fonts/optimized/GoogleSansFlex-latin-all.woff2?url&no-inline";
import { cn } from "@wags/ui/ui-utils";
import { lazy, Suspense, useRef } from "react";
import { RootSearchSchema } from "./root-route.contract";
import appCss from "@/app.css?url";
import { Footer } from "@/components/shared/Footer";
import { NotFound } from "@/components/shared/NotFound";
import { RouteErrorBoundary } from "./route-error-boundary";

import { afterFirstLcp } from "@wags/tanstack-start/after-first-lcp.ts";
import { viewerChatBootstrapQueryOptions } from "@/features/kits-conversations";
import { loadAppShellKitsCopilot } from "./load-kits-copilot";
import {
    registerContactVerifiedRefresh,
    useKitsCopilotEnabled,
    useChatController,
} from "@/features/kits-conversations";
import {
    AppShellProviders,
    AppShellAuthAnalytics,
} from "./app-shell-providers";
import { AppShellNavigation } from "./shell-navigation";
import {
    AUTH_QUERY_KEY,
    authQueryOptions,
    readRequestAuthState,
    seedAuthState,
} from "@/lib/core/auth";
import { isKLinkPath } from "@/lib/core/short-links/short-link-attribution";
import { withTimeout } from "@/lib/core/with-timeout";
import { ExperimentArmsWarmup } from "@/lib/core/experiments/use-experiment-arm";
import {
    useIsMobileViewport,
    useUnsafeEffect,
} from "@wags/tanstack-start/react";
import { getInitialGeoLocation } from "@/features/viewer-location";
import { BOTTOM_NAV_CONTENT_PADDING } from "@/lib/ui/bottomNav";
import { FAVICON_LINKS } from "./favicon-links";
import { isStandalonePage, routeHasBottomNav } from "./shell-navigation-layout";
import { LISTING_MEDIA_PUBLIC_HOSTS } from "@wags/utils/media.ts";
import { DOG_CARD_PHOTO_REVEAL_SCRIPT } from "@/features/listings";
import { sanitizeSpeedInsightsUrl } from "./speed-insights-url";

import { KitsViewStateHost } from "./kits-view-state-host";
import { useContactVerifiedRefresh } from "./chat/use-contact-verified-refresh";
import { RouteBrowserEdges } from "./route-browser-edges";

const InlineKitsCopilot = lazy(() =>
    loadAppShellKitsCopilot().then((mod) => ({
        default: mod.InlineKitsCopilot,
    })),
);

const ROOT_CHAT_BOOTSTRAP_TIMEOUT_MS = 2500;

export type AppShellRouteContext = {
    dbClient: DbClient;
    queryClient: QueryClient;
};

export const appShellRootRouteOptions = {
    validateSearch: RootSearchSchema,
    beforeLoad: async ({
        context,
        location,
    }: {
        context: AppShellRouteContext;
        location: { pathname: string };
    }) => {
        const initialGeo =
            typeof window === "undefined"
                ? await getInitialGeoLocation()
                : null;
        if (location.pathname.startsWith("/api")) {
            return { initialGeo };
        }

        if (location.pathname.startsWith("/internal")) {
            await context.queryClient.ensureQueryData({
                ...authQueryOptions(),
                revalidateIfStale: true,
            });

            return { initialGeo };
        }

        if (
            location.pathname.startsWith("/application") ||
            location.pathname.startsWith("/demo") ||
            isStandalonePage(location.pathname) ||
            location.pathname === "/preview-split" ||
            isKLinkPath(location.pathname)
        ) {
            return { initialGeo };
        }

        const bootstrap = await withTimeout(
            context.queryClient.ensureQueryData(
                viewerChatBootstrapQueryOptions(),
            ),
            ROOT_CHAT_BOOTSTRAP_TIMEOUT_MS,
        ).catch(() => {
            console.error("[root] chat bootstrap failed", {
                pathname: location.pathname,
            });
            return null;
        });

        if (!bootstrap) {
            const existing = await readRequestAuthState().catch(
                () => undefined,
            );
            if (existing !== undefined) {
                context.queryClient.setQueryData(AUTH_QUERY_KEY, existing);
            }
            return { initialGeo };
        }

        seedAuthState(context.queryClient, bootstrap.authState);

        return { initialGeo };
    },
    head: () => ({
        meta: [
            {
                charSet: "utf-8",
            },
            {
                name: "viewport",
                content:
                    "width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=resizes-content",
            },
            {
                title: "Wags Discovery",
            },
            {
                name: "theme-color",
                content: EDGE_COLORS.malteseWhite,
            },
            {
                name: "color-scheme",
                content: "light",
            },
        ],
        links: [
            {
                rel: "stylesheet",
                href: appCss,
            },
            ...FAVICON_LINKS,
            {
                rel: "apple-touch-icon",
                sizes: "192x192",
                href: "/favicon-light-192.png",
            },
            {
                rel: "manifest",
                href: "/manifest.json",
            },
            {
                rel: "preconnect",
                href: "https://dog.ceo",
            },
            {
                rel: "dns-prefetch",
                href: "https://dog.ceo",
            },
            {
                rel: "preconnect",
                href: `https://${LISTING_MEDIA_PUBLIC_HOSTS[0]}`,
            },
            {
                rel: "preload",
                href: grenetteHeroFont,
                as: "font",
                type: "font/woff2",
                crossOrigin: "anonymous" as const,
            },
            {
                rel: "preload",
                href: googleSansFlexLatinFont,
                as: "font",
                type: "font/woff2",
                crossOrigin: "anonymous" as const,
                fetchPriority: "low" as const,
            },
        ],
        scripts: [{ children: DOG_CARD_PHOTO_REVEAL_SCRIPT }],
    }),
    notFoundComponent: () => (
        <div className="flex min-h-dvh w-full items-center justify-center bg-true-white">
            <NotFound />
        </div>
    ),
    errorComponent: RouteErrorBoundary,
    shellComponent: RootComponent,
};

function RootComponent() {
    return (
        <AppShellProviders>
            {import.meta.env.PROD && <VercelSpeedInsights />}
            <AppShellAuthAnalytics />
            <RootDocument>
                <Outlet />
            </RootDocument>
        </AppShellProviders>
    );
}

function VercelSpeedInsights() {
    const routeId = useRouterState({
        select: (state) => state.matches.at(-1)?.routeId ?? null,
    });
    const route = routeId?.startsWith("/") ? routeId : null;

    return (
        <SpeedInsights
            sampleRate={0.1}
            route={route}
            beforeSend={(event) => {
                if (!route) return null;
                const url = sanitizeSpeedInsightsUrl(
                    event.url,
                    route,
                    window.location.origin,
                );
                if (!url) return null;
                return {
                    ...event,
                    route,
                    url,
                };
            }}
        />
    );
}

const HIDE_FOOTER_PREFIXES = [
    "/application",
    "/discover",
    "/journey",
    "/filter-quiz",
    "/find",
    "/dog",
    "/provider",
    "/demo",
    "/match",
    "/wrapped",
    "/internal",
    "/saved",
    "/profile",
    "/settings",
    "/sign-in",
    "/sign-up",
    "/kennel",
];
const HIDE_FOOTER_EXACT = ["/"];

function DeferredKitsCopilot() {
    const isMobileViewport = useIsMobileViewport();
    const { data: ready = false } = useQuery({
        queryKey: ["deferred-kits-copilot-ready"],
        queryFn: ({ signal }) =>
            new Promise<boolean>((resolve) => {
                let settled = false;
                const finish = () => {
                    if (settled || signal.aborted) return;
                    settled = true;
                    document.removeEventListener("pointerdown", finish);
                    document.removeEventListener("keydown", finish);
                    resolve(true);
                };
                signal.addEventListener(
                    "abort",
                    () => {
                        settled = true;
                        document.removeEventListener("pointerdown", finish);
                        document.removeEventListener("keydown", finish);
                    },
                    { once: true },
                );
                document.addEventListener("pointerdown", finish, {
                    once: true,
                });
                document.addEventListener("keydown", finish, { once: true });
                void afterFirstLcp(signal).then(finish);
            }),
        enabled: typeof window !== "undefined",
        staleTime: Number.POSITIVE_INFINITY,
    });
    if (!ready) return null;
    return (
        <Suspense fallback={null}>
            <KitsViewStateHost />
            {isMobileViewport && <ExperimentArmsWarmup />}
            <InlineKitsCopilot />
        </Suspense>
    );
}

function pathMatches(pathname: string, prefixes: string[], exact: string[]) {
    return (
        exact.includes(pathname) ||
        prefixes.some(
            (prefix) =>
                pathname === prefix || pathname.startsWith(`${prefix}/`),
        )
    );
}

function canShowCopilot(pathname: string) {
    return (
        ![
            "/filter-quiz",
            "/application",
            "/demo",
            "/internal",
            "/feedback",
            "/kennel",
        ].some((prefix) => pathname.startsWith(prefix)) &&
        !isStandalonePage(pathname)
    );
}

function ContactVerifiedRefreshRegistration() {
    const refresh = useContactVerifiedRefresh();
    useUnsafeEffect(() => registerContactVerifiedRefresh(refresh));
    return null;
}

function RootDocument({ children }: { children: React.ReactNode }) {
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });
    const footer = useRef<HTMLElement>(null);
    const copilotEnabled = useKitsCopilotEnabled();
    const contactRequested = useChatController(
        (state) => state.pendingContact !== null,
    );

    const showFooter =
        !isStandalonePage(pathname) &&
        !pathMatches(pathname, HIDE_FOOTER_PREFIXES, HIDE_FOOTER_EXACT);

    return (
        <AppShellNavigation pathname={pathname}>
            {({ reserveBottomNavSpace, navigation }) => (
                <html lang="en">
                    <head>
                        <HeadContent />
                    </head>
                    <body className="antialiased overscroll-none">
                        <a
                            href="#main-content"
                            className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-100 focus:rounded-sm focus:bg-labrador-yellow focus:px-4 focus:py-2 focus:text-label focus:text-doberman-black focus:shadow-lg"
                        >
                            Skip to content
                        </a>
                        <RouteBrowserEdges footer={footer} />
                        <div id="app-scroll">
                            <main
                                id="main-content"
                                className={cn(
                                    "min-h-dvh",

                                    pathname === "/faq" && "bg-true-white",

                                    reserveBottomNavSpace &&
                                        `md:pb-0 ${BOTTOM_NAV_CONTENT_PADDING}`,
                                )}
                            >
                                {children}
                            </main>

                            <div id="site-footer">
                                {showFooter && (
                                    <Footer
                                        key={pathname}
                                        ref={footer}
                                        surfaceClassName={
                                            pathname === "/faq"
                                                ? "-top-px bg-true-white"
                                                : undefined
                                        }
                                        reserveBottomNav={routeHasBottomNav(
                                            pathname,
                                        )}
                                    />
                                )}
                            </div>
                        </div>
                        <ContactVerifiedRefreshRegistration />
                        {canShowCopilot(pathname) && (
                            <>
                                {(copilotEnabled || contactRequested) && (
                                    <DeferredKitsCopilot />
                                )}
                            </>
                        )}
                        {navigation}

                        <Scripts />
                    </body>
                </html>
            )}
        </AppShellNavigation>
    );
}
